import cloneDeep from 'lodash/cloneDeep';
import { reaction } from 'mobx';
import { applySnapshot, destroy, flow, types } from 'mobx-state-tree';
import moment from 'moment';
import { downloadBlobFile, killNulls } from 'utils';
import { CreateModeType, IAppeal, IAppealCategory, IComment } from './types';
import { AppealsStatsEnum, AppealCreateMode } from './constants';
import { Appeals } from 'http-requests';
import {
  AppealCategoryModel,
  AppealManagementModel,
  AppealModel,
  AppealTopicsListModel,
  CommentModel,
  FiltersModel,
  GISModel,
  OthersModel,
  StatusesModel,
} from './models';
import { ISelectValue } from 'components/ui';
import { GetAppealsWithSameProblem } from '../../../http-requests/appeals';

const AppealsStatsModel = types
  .model('GetAppealsStats', {
    activeTab: types.optional(
      types.maybeNull(types.enumeration(AppealsStatsEnum)),
      null
    ),
    run: types.optional(types.number, 0),
    new: types.optional(types.number, 0),
    performed: types.optional(types.number, 0),
    overdue: types.optional(types.number, 0),
  })
  .actions((self) => ({
    setActiveTab(tab: typeof self.activeTab) {
      self.activeTab = tab;
    },
    incrementStat<K extends keyof typeof self>(fieldName: K) {
      self[fieldName] += 1;
    },
    decrementStat<K extends keyof typeof self>(fieldName: K) {
      self[fieldName] = Math.max(self[fieldName] - 1, 0);
    },
  }));

export const AppealsStore = types
  .model('AppealsStore', {
    statuses: types.optional(StatusesModel, {}),
    filters: types.optional(FiltersModel, {}),
    list: types.optional(types.array(AppealModel), []),
    stats: types.optional(AppealsStatsModel, {}),
    topicsList: types.optional(types.array(AppealTopicsListModel), []),
    categories: types.optional(types.array(AppealCategoryModel), []),
    editableAppeal: types.optional(
      types.maybeNull(types.safeReference(AppealModel)),
      null
    ),
    comments: types.optional(types.array(CommentModel), []),
    newAppeal: types.optional(AppealModel, {}),
    others: types.optional(OthersModel, {}),
    management: types.optional(AppealManagementModel, {}),
    GIS: types.optional(GISModel, {}),
    createMode: types.optional(types.enumeration(AppealCreateMode), 'default'),
    ourSPBAvailability: types.optional(types.boolean, false),
  })
  .volatile(() => ({
    totalAppeals: 0,
  }))
  .views((self) => ({
    getCategoryName(id: string): string | undefined {
      return self.categories.find((el) => el._id === id)?.name;
    },
    hasCategoryName(categoryName: string): boolean {
      return self.categories.some(
        (category) =>
          category.name.trim().toLowerCase() ===
          categoryName.trim().toLowerCase()
      );
    },
    get categoryOptions(): ISelectValue[] {
      return self.categories.map((el) => ({
        label: el.name,
        value: el._id,
      }));
    },
  }))
  .actions((self) => ({
    afterCreate() {
      /* Устанавливаем реакцию, которая будет срабатывать каждый раз,
       * когда изменяется список категорий.
       * Нужно для того, чтобы при удалении категории
       * в модальном окне "Создание категорий", удаленная категория пропадала
       * из всех обращений, в которых она используется.
       */
      reaction(
        () => self.categories.map((cat) => cat._id),
        (categoryIDs) => {
          self.list.forEach((appeal) => {
            appeal.category_ids.replace(
              appeal.category_ids.filter((id) => categoryIDs.includes(id))
            );
          });
        }
      );

      self.newAppeal.updateField(
        'source',
        self.createMode === 'portal' ? 'our_spb' : 'dispatcher'
      );
    },
    prependNewAppeal(newAppeal: IAppeal) {
      const proccessedAppeal = AppealModel.create(newAppeal);
      proccessedAppeal.markNew();
      self.list.unshift(proccessedAppeal);
    },
    setCreateMode(value: CreateModeType) {
      self.createMode = value;
      self.newAppeal.updateField(
        'source',
        value === 'portal' ? 'our_spb' : 'dispatcher'
      );
    },
    startEditing(appeal: IAppeal) {
      self.editableAppeal = appeal;
    },
    clearComments() {
      self.comments.clear();
    },
    appendNewComment(newComment: IComment) {
      self.comments.unshift(newComment);
    },
    clearAll() {
      applySnapshot(self, {});
    },
    getAppealsList: flow(function* (): Generator<
      Promise<any>,
      IAppeal[] | void,
      any
    > {
      try {
        self.statuses.list.loadingStates();
        self.filters.restoreFromLocalStorage();

        const response = yield Appeals.GetAppeals(
          killNulls(self.filters.backendPayload)
        );

        // Всегда очищаем список перед добавлением новых данных
        self.list.clear();
        self.list.replace(response.result);

        self.totalAppeals = response.count;
        self.statuses.list.successStates();

        return self.list;
      } catch (error) {
        self.statuses.list.errorStates();
        console.error(error);

        throw error;
      } finally {
        self.statuses.list.finallyStates();
      }
    }),
    getAppealsStats: flow(function* () {
      try {
        self.statuses.stats.loadingStates();
        self.stats = yield Appeals.GetAppealsStats();
        self.statuses.stats.successStates();

        return self.stats;
      } catch (error) {
        self.statuses.stats.errorStates();
        console.error(error);

        throw error;
      } finally {
        self.statuses.stats.finallyStates();
      }
    }),
    getHistoryAppeals: flow(function* () {
      try {
        self.statuses.history.loadingStates();
        const result: IAppeal[] = [];

        const response = yield Appeals.GetAppeals(
          killNulls(self.filters.backendPayload)
        );
        response.result.forEach((el: Partial<IAppeal>) => {
          result.push(AppealModel.create(el));
        });
        self.statuses.history.successStates();

        return result;
      } catch (error) {
        self.statuses.history.errorStates();
        console.error(error);

        throw error;
      } finally {
        self.statuses.history.finallyStates();
      }
    }),
    getAppeal: flow(function* (appealID: string) {
      try {
        self.statuses.getAppeal.loadingStates();
        const response = yield Appeals.GetAppealByID(appealID);

        const existingAppealIndex = self.list.findIndex(
          (appeal) => appeal._id === appealID
        );

        if (existingAppealIndex === -1) {
          self.list.push(response);
          self.editableAppeal = self.list[self.list.length - 1];
        } else {
          self.list[existingAppealIndex] = response;
          self.editableAppeal = self.list[existingAppealIndex];
        }

        self.statuses.getAppeal.successStates();

        return response;
      } catch (error) {
        self.statuses.getAppeal.errorStates();
        console.error(error);

        throw error;
      } finally {
        self.statuses.getAppeal.finallyStates();
      }
    }),
    deleteAppeal: flow(function* (appeal: IAppeal) {
      try {
        self.statuses.deleteAppeal.loadingStates();
        yield Appeals.DeleteAppeal(appeal._id);
        destroy(appeal);
        self.statuses.deleteAppeal.successStates();
      } catch (error) {
        self.statuses.deleteAppeal.errorStates();
        console.error(error);

        throw error;
      } finally {
        self.statuses.deleteAppeal.finallyStates();
      }
    }),
    getCategories: flow(function* () {
      try {
        self.statuses.categories.loadingStates();
        const response = yield Appeals.GetAppealCategories();
        self.categories.replace(response);
        self.statuses.categories.successStates();

        return response;
      } catch (error) {
        self.statuses.categories.errorStates();
        console.error(error);
      } finally {
        self.statuses.categories.finallyStates();
      }
    }),
    getComments: flow(function* (appealID: string) {
      try {
        self.statuses.getComments.loadingStates();

        const response = yield Appeals.GetComments(appealID);
        self.comments = response;
        self.statuses.getComments.successStates();

        return response;
      } catch (error) {
        self.statuses.getComments.errorStates();
        console.error(error);

        throw error;
      } finally {
        self.statuses.getComments.finallyStates();
      }
    }),
    createCategory: flow(function* (data: {
      name: string;
      description: string;
    }) {
      try {
        self.statuses.createCategory.loadingStates();

        const response = yield Appeals.CreateAppealCategory(data);
        self.categories.unshift(response);
        self.statuses.createCategory.successStates();

        return response;
      } catch (error) {
        self.statuses.createCategory.errorStates();
        console.error(error);

        throw error;
      } finally {
        self.statuses.createCategory.finallyStates();
      }
    }),
    deleteCategory: flow(function* (category: IAppealCategory) {
      try {
        category.deleteStatus.loadingStates();
        yield Appeals.DeleteAppealCategory(category._id);
        category.deleteStatus.successStates();
        destroy(category);
      } catch (error) {
        category.deleteStatus.errorStates();
        console.error(error);

        throw error;
      } finally {
        category.deleteStatus.finallyStates();
      }
    }),
    getTopics: flow(function* () {
      try {
        self.statuses.topicsList.loadingStates();
        self.topicsList = yield Appeals.GetTopics();
        self.statuses.topicsList.successStates();

        return self.topicsList;
      } catch (error) {
        self.statuses.topicsList.errorStates();
        console.error(error);

        throw error;
      } finally {
        self.statuses.topicsList.finallyStates();
      }
    }),
    downloadXLSXReport: flow(function* () {
      try {
        self.statuses.xlsxReport.loadingStates();

        const startDate = moment(self.filters.created_at__gte);
        const maxEndDate = cloneDeep(startDate).add(6, 'months');
        const endDate = moment(self.filters.created_at__lt);

        if (endDate.isAfter(maxEndDate)) {
          throw new Error(
            'Период, указанный в поле «Дата подачи», не должен превышать полгода'
          );
        }

        const shouldApplyCreatedAtFilter =
          !self.filters.area___id__in?.length &&
          !self.filters.house___id__in?.length;

        const response = yield Appeals.DownloadXLSXReport(
          killNulls({
            ...self.filters,
            limit: 0,
            offset: null,
            ...(shouldApplyCreatedAtFilter && {
              created_at__gte:
                self.filters.created_at__gte ??
                moment().subtract(6, 'months').toISOString(),
            }),
          })
        );

        // Иногда 500 не попадает в catch
        if (!response.ok) {
          throw new Error();
        }

        const blob = yield response.blob();
        downloadBlobFile(blob, 'Отчёт (Обращения).xlsx');

        self.statuses.xlsxReport.successStates();

        return response;
      } catch (error) {
        self.statuses.xlsxReport.errorStates();
        console.error(error);

        throw error;
      } finally {
        self.statuses.xlsxReport.finallyStates();
      }
    }),
    getOurSPBAvailability: flow(function* () {
      try {
        self.statuses.getOurSpbAvailability.loadingStates();

        const response = yield Appeals.GetOurSPBAvailability();
        self.ourSPBAvailability = response.is_available;

        self.statuses.getOurSpbAvailability.successStates();
        return response;
      } catch (e) {
        console.error(e);
        self.statuses.getOurSpbAvailability.errorStates();
      } finally {
        self.statuses.getOurSpbAvailability.finallyStates();
      }
    }),
    getAppealsWithSameProblem: flow(function* (problemNumber: string) {
      try {
        self.statuses.getAppealsWithSameProblem.loadingStates();

        const response = yield Appeals.GetAppealsWithSameProblem(problemNumber);

        self.statuses.getAppealsWithSameProblem.successStates();
        return response;
      } catch (e) {
        console.error(e);
        self.statuses.getAppealsWithSameProblem.errorStates();
      } finally {
        self.statuses.getAppealsWithSameProblem.finallyStates();
      }
    }),
  }));
